/**
 * Loader for Apple's Sign in with Apple JS, the script that runs the Apple
 * authorization popup and hands back an identity token.
 *
 * The counterpart to googleIdentity, and the same reasoning applies: the token
 * it produces is worthless on its own, because the API verifies the signature,
 * issuer and audience before it will mint a session. Nothing here is a security
 * boundary, so a blocked or slow script is treated as "Apple sign-in is
 * unavailable" rather than an error the user has to act on.
 */

import { isEmbeddedWebView } from "./googleIdentity";

export interface AppleAuthorization {
  id_token?: string;
  code?: string;
  state?: string;
}

/**
 * Apple sends the name and email only on the user's very first authorization
 * for this Services ID, and never again.
 */
export interface AppleUser {
  name?: { firstName?: string; lastName?: string };
  email?: string;
}

export interface AppleSignInResponse {
  authorization?: AppleAuthorization;
  user?: AppleUser;
}

interface AppleIDAuth {
  init(config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    state?: string;
    nonce?: string;
    usePopup: boolean;
  }): void;
  signIn(): Promise<AppleSignInResponse>;
}

interface AppleIDGlobal {
  auth?: AppleIDAuth;
}

const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const APPLE_SCRIPT_ID = "apple-signin-js";
const LOAD_TIMEOUT_MS = 10_000;

const getAppleGlobal = (): AppleIDGlobal | undefined =>
  (window as Window & { AppleID?: AppleIDGlobal }).AppleID;

/**
 * The Services ID, injected at build time.
 *
 * Not the app's bundle identifier: Apple treats the website as a separate
 * client, so the web flow authenticates against a Services ID registered with
 * this site's domains and return URLs. Apple sign-in is hidden entirely when it
 * is unset, so an environment that has not been configured degrades to the
 * other options rather than showing a button that cannot work.
 */
export const getAppleClientId = (): string | undefined => {
  const clientId = process.env.REACT_APP_APPLE_SERVICES_ID?.trim();
  return clientId ? clientId : undefined;
};

/**
 * The path the Services ID's return URLs are registered under.
 *
 * Apple matches the redirect URI against the registered list exactly, path and
 * all, so the bare origin is rejected with "Invalid web redirect url" even
 * though the host itself is registered. That is what broke the first release:
 * the origin alone looks right and is not.
 */
const RETURN_URL_PATH = "/login";

/**
 * Where Apple sends the browser back.
 *
 * Apple validates this against the return URLs registered on the Services ID
 * even in popup mode, where nothing is actually redirected, so it has to be a
 * registered URL exactly.
 *
 * Derived from the current origin rather than hard-coded, because production
 * serves two hostnames from a single build. Pinning one would break the other:
 * in popup mode Apple posts the credential back to the redirect URI's origin,
 * so a page on the host that was not named would never receive an answer and
 * the popup would simply hang. Each host therefore names itself, and each is
 * registered with Apple separately.
 *
 * The override stays for an environment whose return URL does not follow the
 * pattern, and it must be a full URL including the path.
 */
export const getAppleRedirectUri = (): string =>
  process.env.REACT_APP_APPLE_REDIRECT_URI?.trim() ||
  `${window.location.origin}${RETURN_URL_PATH}`;

/**
 * Whether the button can work here.
 *
 * Unlike Google's, Apple's web flow runs in any modern browser, including on
 * Windows and Android, so there is no platform gate: someone who signed up with
 * Apple on their iPhone can sign in from a Windows desktop.
 *
 * The exception is our own app's WebView, where the native Sign in with Apple
 * button is already on screen and Apple's popup is unreliable. Showing a second,
 * worse route to the same thing there helps nobody.
 */
export const isAppleSignInAvailable = (): boolean =>
  getAppleClientId() !== undefined && !isEmbeddedWebView();

let loadPromise: Promise<AppleIDAuth> | null = null;

/**
 * Resolves with Apple's auth API once the script is ready. The script is
 * injected at most once per page; concurrent callers share one promise. A
 * failed load clears the promise so a later mount can retry.
 */
export const loadAppleIdentityServices = (): Promise<AppleIDAuth> => {
  const ready = getAppleGlobal()?.auth;
  if (ready) return Promise.resolve(ready);

  if (loadPromise) return loadPromise;

  loadPromise = new Promise<AppleIDAuth>((resolve, reject) => {
    let settled = false;

    const finish = () => {
      const api = getAppleGlobal()?.auth;
      if (settled) return;
      if (!api) {
        settled = true;
        loadPromise = null;
        reject(new Error("Sign in with Apple JS loaded without an auth API"));
        return;
      }
      settled = true;
      resolve(api);
    };

    const fail = (reason: string) => {
      if (settled) return;
      settled = true;
      loadPromise = null;
      reject(new Error(reason));
    };

    const timer = window.setTimeout(
      () => fail("Sign in with Apple JS timed out"),
      LOAD_TIMEOUT_MS
    );

    const existing = document.getElementById(
      APPLE_SCRIPT_ID
    ) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");

    script.addEventListener("load", () => {
      window.clearTimeout(timer);
      finish();
    });
    script.addEventListener("error", () => {
      window.clearTimeout(timer);
      script.remove();
      fail("Sign in with Apple JS failed to load");
    });

    if (!existing) {
      script.id = APPLE_SCRIPT_ID;
      script.src = APPLE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  return loadPromise;
};

/** Test seam: forget any in-flight or completed script load. */
export const resetAppleIdentityLoaderForTests = (): void => {
  loadPromise = null;
};

/**
 * Why a sign-in produced no token.
 *
 * A stable machine code, deliberately separate from `message`: the message is
 * user-facing copy that will be reworded, while this rides on the
 * sso_sign_in_failed / sso_sign_in_abandoned events, where a silent rename
 * would break the funnel.
 */
export type AppleSignInReason =
  | "cancelled"
  | "not_configured"
  | "script_unavailable"
  | "no_token"
  | "state_mismatch"
  | "unknown";

/** Thrown for every failure the caller should surface to the user. */
export class AppleSignInError extends Error {
  readonly cancelled: boolean;
  readonly reason: AppleSignInReason;

  constructor(
    message: string,
    cancelled = false,
    reason: AppleSignInReason = "unknown"
  ) {
    super(message);
    this.name = "AppleSignInError";
    this.cancelled = cancelled;
    this.reason = reason;
  }
}

export interface AppleCredential {
  identityToken: string;
  authorizationCode?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Where the first-authorization profile is held.
 *
 * Apple returns the user's name only on the very first authorization for this
 * Services ID. Every later sign-in returns nothing, and there is no way to ask
 * again short of the user removing Greenback from their Apple ID settings. So
 * if the popup succeeds and the call to our own API then fails, that name is
 * gone for good and the account is created nameless later.
 *
 * Keeping it the moment Apple hands it over, and clearing it only once the API
 * has accepted a sign-in, turns that one irrecoverable shot into a retry. The
 * mobile app does the same thing in SecureStore.
 */
const PROFILE_KEY = "apple_signin_first_profile";

interface StoredProfile {
  sub: string;
  firstName?: string;
  lastName?: string;
}

/**
 * The `sub` claim, read without verifying anything.
 *
 * Safe because it is used only as a cache key: the API re-reads every claim
 * from the signed token and trusts nothing sent alongside it.
 */
const subjectOf = (identityToken: string): string | undefined => {
  try {
    const payload = identityToken.split(".")[1];
    if (!payload) return undefined;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return (JSON.parse(json) as { sub?: string }).sub;
  } catch {
    return undefined;
  }
};

const readStoredProfile = (): StoredProfile | null => {
  try {
    const raw = window.localStorage.getItem(PROFILE_KEY);
    return raw ? (JSON.parse(raw) as StoredProfile) : null;
  } catch {
    // Storage disabled or the entry is corrupt, which is no worse than never
    // having had one.
    return null;
  }
};

const writeStoredProfile = (profile: StoredProfile): void => {
  try {
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch {
    // Best effort: failing to cache must not fail the sign-in itself.
  }
};

/**
 * Called once the API has accepted a sign-in, at which point the profile has
 * been persisted server-side and the local copy has no further job.
 */
export const clearStoredAppleProfile = (): void => {
  try {
    window.localStorage.removeItem(PROFILE_KEY);
  } catch {
    // Nothing stored, or storage is unavailable.
  }
};

/**
 * An opaque value Apple echoes back, so a response can be matched to the
 * request that asked for it.
 *
 * Falls back to Math.random where crypto is missing. That is weaker, and fine
 * here: this value never leaves the page and is never presented to anything as
 * proof. It exists to catch a response that did not come from this request,
 * not to resist an attacker who cannot see it in the first place. Refusing to
 * sign in at all would be the worse trade.
 */
const randomState = (): string => {
  const bytes = new Uint8Array(16);

  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
};

/**
 * Runs Apple's popup and returns the credential.
 *
 * Throws AppleSignInError, with `cancelled` set when the user simply closed the
 * popup, so the caller can stay silent rather than showing an error for a
 * deliberate dismissal.
 */
export const signInWithApple = async (): Promise<AppleCredential> => {
  const clientId = getAppleClientId();
  if (!clientId) {
    throw new AppleSignInError(
      "Apple sign-in is not available.",
      false,
      "not_configured"
    );
  }

  let auth: AppleIDAuth;
  try {
    auth = await loadAppleIdentityServices();
  } catch {
    throw new AppleSignInError(
      "Apple sign-in is unavailable right now. Please try again.",
      false,
      "script_unavailable"
    );
  }

  const state = randomState();

  let response: AppleSignInResponse;
  try {
    auth.init({
      clientId,
      scope: "name email",
      redirectURI: getAppleRedirectUri(),
      state,
      usePopup: true,
    });
    response = await auth.signIn();
  } catch (error) {
    const code = (error as { error?: string })?.error;
    if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
      throw new AppleSignInError("Sign-in cancelled", true, "cancelled");
    }
    throw new AppleSignInError(
      "Apple sign-in failed. Please try again.",
      false,
      "unknown"
    );
  }

  const identityToken = response.authorization?.id_token;
  if (!identityToken) {
    throw new AppleSignInError(
      "Apple did not return a sign-in token. Please try again.",
      false,
      "no_token"
    );
  }

  // Apple echoes back what we sent. A mismatch means the response did not come
  // from the request we made, so it is not one to act on.
  if (response.authorization?.state && response.authorization.state !== state) {
    throw new AppleSignInError(
      "Apple sign-in failed. Please try again.",
      false,
      "state_mismatch"
    );
  }

  const authorizationCode = response.authorization?.code;
  const firstName = response.user?.name?.firstName || undefined;
  const lastName = response.user?.name?.lastName || undefined;
  const sub = subjectOf(identityToken);

  // Populated only on the first authorization, so keep it the moment it
  // appears rather than trusting a single round trip to our API.
  if (firstName || lastName) {
    if (sub) writeStoredProfile({ sub, firstName, lastName });
    return { identityToken, authorizationCode, firstName, lastName };
  }

  // A later sign-in: fall back to whatever the first one left behind, and only
  // for the same Apple user, so a second account in this browser cannot inherit
  // the first one's name.
  const stored = readStoredProfile();
  if (stored && sub && stored.sub === sub) {
    return {
      identityToken,
      authorizationCode,
      firstName: stored.firstName,
      lastName: stored.lastName,
    };
  }

  return { identityToken, authorizationCode };
};
